<template>
  <div class="min-h-screen bg-[#f6f7f9]">

    <!-- ── Loading ── -->
    <div v-if="loading" class="flex flex-col items-center justify-center min-h-[60vh] gap-4">
      <div class="w-10 h-10 rounded-full border-4 border-[#f0f0f0] border-t-[#FFD500] animate-spin"></div>
      <p class="text-[#888] text-[0.9375rem]">Merk laden…</p>
    </div>

    <!-- ── Error ── -->
    <div v-else-if="error" class="flex flex-col items-center justify-center min-h-[60vh] gap-4 px-6 text-center">
      <div class="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center">
        <i class="bi bi-exclamation-triangle text-red-400 text-[1.75rem]"></i>
      </div>
      <h3 class="text-[1.25rem] font-black text-[#111]">Merk niet gevonden</h3>
      <p class="text-[#888] max-w-sm">Het merk dat je zoekt bestaat niet of is niet beschikbaar.</p>
      <router-link to="/merken" class="inline-flex items-center gap-2 bg-[#FFD500] hover:bg-[#ffb800] text-[#111] font-bold px-5 py-3 rounded-xl no-underline transition-all">
        <i class="bi bi-arrow-left"></i> Terug naar merken
      </router-link>
    </div>

    <!-- ── Content ── -->
    <div v-else-if="brand">

      <!-- Hero / Brand header -->
      <section class="bg-white border-b border-[#f0f0f0] py-10 lg:py-14">
        <div class="max-w-screen-xl mx-auto px-6">

          <!-- Breadcrumb -->
          <nav class="flex items-center gap-1.5 text-[0.8rem] text-[#999] mb-6 flex-wrap">
            <router-link to="/" class="hover:text-[#111] transition-colors no-underline"><i class="bi bi-house"></i></router-link>
            <i class="bi bi-chevron-right text-[0.6rem]"></i>
            <router-link to="/merken" class="hover:text-[#111] transition-colors no-underline">Merken</router-link>
            <i class="bi bi-chevron-right text-[0.6rem]"></i>
            <span class="text-[#333] font-medium">{{ brand.name }}</span>
          </nav>

          <div class="flex flex-col sm:flex-row gap-8 items-start">
            <!-- Logo -->
            <div class="shrink-0 w-36 h-28 lg:w-44 lg:h-32 rounded-2xl border border-[#e9eaec] bg-[#fafafa] flex items-center justify-center overflow-hidden">
              <img
                v-if="brand.image?.src"
                :src="brand.image.src"
                :alt="brand.name"
                class="w-full h-full object-contain p-4"
              />
              <span v-else class="text-[2rem] font-black text-[#ddd] tracking-tight">
                {{ brand.name.slice(0, 2).toUpperCase() }}
              </span>
            </div>

            <!-- Info -->
            <div class="flex-1">
              <h1 class="text-[clamp(1.75rem,4vw,2.5rem)] font-black text-[#111] leading-tight mb-3">
                {{ brand.name }}
              </h1>
              <p v-if="brand.description" class="text-[1rem] text-[#555] leading-relaxed mb-4 max-w-xl" v-html="brand.description"></p>

              <!-- Stats -->
              <div class="flex flex-wrap gap-3">
                <span class="inline-flex items-center gap-2 bg-[#f6f7f9] border border-[#e9eaec] text-[#555] text-[0.82rem] font-semibold px-3.5 py-2 rounded-xl">
                  <i class="bi bi-box-seam text-[#FFD500]"></i> {{ totalProducts }} producten
                </span>
                <span class="inline-flex items-center gap-2 bg-[#f6f7f9] border border-[#e9eaec] text-[#555] text-[0.82rem] font-semibold px-3.5 py-2 rounded-xl">
                  <i class="bi bi-shield-check text-[#FFD500]"></i> Officiële dealer
                </span>
                <span class="inline-flex items-center gap-2 bg-[#f6f7f9] border border-[#e9eaec] text-[#555] text-[0.82rem] font-semibold px-3.5 py-2 rounded-xl">
                  <i class="bi bi-truck text-[#FFD500]"></i> Gratis verzending vanaf €50
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ── Categorie-tabs (sticky) ── -->
      <div class="bg-white border-b border-[#f0f0f0] sticky top-[var(--header-height,124px)] z-30 shadow-sm">
        <div class="max-w-screen-xl mx-auto px-6">
          <div class="flex items-center gap-2 py-3 overflow-x-auto scrollbar-hide">
            <span class="text-[0.78rem] font-bold text-[#999] uppercase tracking-widest shrink-0 mr-2 hidden sm:block">Categorie:</span>

            <router-link
              :to="`/merk/${brandSlug}`"
              class="inline-flex items-center gap-1.5 shrink-0 px-4 py-2 rounded-full text-[0.82rem] font-semibold no-underline transition-all border"
              :class="!selectedCategory
                ? 'bg-[#FFD500] border-[#FFD500] text-[#111]'
                : 'bg-transparent border-[#e9eaec] text-[#555] hover:border-[#FFD500] hover:text-[#111]'"
            >
              <i class="bi bi-grid-3x3-gap text-[0.75rem]"></i>
              Alles
              <span class="text-[0.72rem] opacity-60">{{ totalProducts }}</span>
            </router-link>

            <router-link
              v-for="cat in categories"
              :key="cat.id"
              :to="`/merk/${brandSlug}/${cat.slug}`"
              class="inline-flex items-center gap-1.5 shrink-0 px-4 py-2 rounded-full text-[0.82rem] font-semibold no-underline transition-all border"
              :class="selectedCategory === cat.slug
                ? 'bg-[#FFD500] border-[#FFD500] text-[#111]'
                : 'bg-transparent border-[#e9eaec] text-[#555] hover:border-[#FFD500] hover:text-[#111]'"
            >
              <i :class="`bi ${getCategoryIcon(cat.slug)} text-[0.75rem]`"></i>
              {{ decodeHtml(cat.name) }}
              <span class="text-[0.72rem] opacity-60">{{ cat.count }}</span>
            </router-link>
          </div>
        </div>
      </div>

      <!-- ── Filter / Sortering bar ── -->
      <div class="max-w-screen-xl mx-auto px-6 py-4 flex items-center justify-between gap-4 flex-wrap">
        <p class="text-[0.875rem] text-[#888] font-medium">
          <span class="text-[#111] font-bold">{{ filteredProducts.length }}</span>
          {{ filteredProducts.length === 1 ? 'product' : 'producten' }}
          <span v-if="selectedCategoryName"> in <em>{{ selectedCategoryName }}</em></span>
        </p>

        <div class="flex items-center gap-3">
          <!-- Sortering -->
          <div class="flex items-center gap-2">
            <label class="text-[0.8rem] text-[#888] font-medium hidden sm:block">Sorteren:</label>
            <select
              v-model="sortBy"
              class="border border-[#e9eaec] rounded-xl px-3 py-2 text-[0.8rem] font-semibold text-[#111] bg-white focus:outline-none focus:border-[#FFD500] cursor-pointer"
            >
              <option value="popularity">Populairste</option>
              <option value="date">Nieuwste</option>
              <option value="price">Prijs: laag → hoog</option>
              <option value="price-desc">Prijs: hoog → laag</option>
            </select>
          </div>

          <!-- Grid / List toggle -->
          <div class="flex bg-[#f6f7f9] rounded-xl p-1 gap-1">
            <button
              @click="viewMode = 'grid'"
              :class="viewMode === 'grid' ? 'bg-[#FFD500] text-[#111]' : 'text-[#888] hover:text-[#111]'"
              class="w-8 h-8 rounded-lg flex items-center justify-center transition-all"
              title="Grid"
            >
              <i class="bi bi-grid text-[0.875rem]"></i>
            </button>
            <button
              @click="viewMode = 'list'"
              :class="viewMode === 'list' ? 'bg-[#FFD500] text-[#111]' : 'text-[#888] hover:text-[#111]'"
              class="w-8 h-8 rounded-lg flex items-center justify-center transition-all"
              title="Lijst"
            >
              <i class="bi bi-list-ul text-[0.875rem]"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- ── Producten ── -->
      <section class="max-w-screen-xl mx-auto px-6 pb-16">

        <!-- Laden -->
        <div v-if="productsLoading" class="flex flex-col items-center justify-center py-20 gap-4">
          <div class="w-10 h-10 rounded-full border-4 border-[#f0f0f0] border-t-[#FFD500] animate-spin"></div>
          <p class="text-[#888] text-[0.9rem]">Producten laden…</p>
        </div>

        <!-- Grid -->
        <div
          v-else-if="filteredProducts.length"
          :class="viewMode === 'grid'
            ? 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-5'
            : 'flex flex-col gap-3'"
        >
          <ProductCard
            v-for="(product, i) in filteredProducts"
            :key="product.id"
            :product="product"
            :index="i"
            :view-mode="viewMode"
          />
        </div>

        <!-- Leeg -->
        <div v-else class="flex flex-col items-center justify-center py-20 gap-4 text-center">
          <div class="w-16 h-16 rounded-2xl bg-[#f0f0f0] flex items-center justify-center">
            <i class="bi bi-box-seam text-[#ccc] text-[1.75rem]"></i>
          </div>
          <h3 class="text-[1.1rem] font-bold text-[#111]">Geen producten gevonden</h3>
          <p class="text-[#888] text-[0.875rem] max-w-xs">
            {{ selectedCategory ? `Er zijn geen ${brand.name} producten in deze categorie.` : `Er zijn momenteel geen ${brand.name} producten beschikbaar.` }}
          </p>
          <router-link :to="`/merk/${brandSlug}`" class="inline-flex items-center gap-2 bg-[#FFD500] hover:bg-[#ffb800] text-[#111] font-bold px-4 py-2.5 rounded-xl no-underline transition-all text-[0.875rem]">
            <i class="bi bi-arrow-left"></i> Alle {{ brand.name }} producten
          </router-link>
        </div>
      </section>

      <!-- ── Over het merk ── -->
      <section v-if="brand.description" class="bg-white border-t border-[#f0f0f0] py-14">
        <div class="max-w-screen-xl mx-auto px-6 max-w-3xl">
          <h2 class="text-[1.25rem] font-black text-[#111] mb-4">Over {{ brand.name }}</h2>
          <div class="text-[0.9375rem] text-[#555] leading-relaxed" v-html="brand.description"></div>
        </div>
      </section>

      <!-- ── USP-banner ── -->
      <section class="bg-gradient-to-r from-[#111] to-[#1a1a1a] py-10">
        <div class="max-w-screen-xl mx-auto px-6">
          <div class="grid grid-cols-2 lg:grid-cols-4 gap-6">
            <div v-for="usp in usps" :key="usp.label" class="flex items-center gap-3">
              <div class="w-10 h-10 rounded-xl bg-[#FFD500]/10 border border-[#FFD500]/20 flex items-center justify-center shrink-0">
                <i :class="`bi ${usp.icon} text-[#FFD500] text-[1rem]`"></i>
              </div>
              <div>
                <div class="text-white font-bold text-[0.875rem] leading-tight">{{ usp.label }}</div>
                <div class="text-white/50 text-[0.75rem]">{{ usp.sub }}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import ProductCard from '@components/ProductCard.vue'

const route = useRoute()

const loading        = ref(true)
const productsLoading = ref(false)
const error          = ref(false)
const brand          = ref(null)
const products       = ref([])
const sortBy         = ref('popularity')
const viewMode       = ref('grid')

const brandSlug        = computed(() => route.params.brand)
const selectedCategory = computed(() => route.params.category || null)

const selectedCategoryName = computed(() => {
  if (!selectedCategory.value) return null
  return categories.value.find(c => c.slug === selectedCategory.value)?.name ?? null
})

const totalProducts = computed(() => products.value.length)

// Unieke categorieën uit producten, zonder 'uncategorized'
const categories = computed(() => {
  const map = new Map()
  products.value.forEach(p =>
    p.categories?.forEach(cat => {
      if (cat.slug === 'uncategorized') return
      if (!map.has(cat.id)) map.set(cat.id, { ...cat, count: 0 })
      map.get(cat.id).count++
    })
  )
  return [...map.values()].sort((a, b) => b.count - a.count)
})

const filteredProducts = computed(() => {
  let list = [...products.value]
  if (selectedCategory.value) {
    list = list.filter(p => p.categories?.some(c => c.slug === selectedCategory.value))
  }
  switch (sortBy.value) {
    case 'date':       list.sort((a, b) => new Date(b.date_created) - new Date(a.date_created)); break
    case 'price':      list.sort((a, b) => parseFloat(a.price) - parseFloat(b.price)); break
    case 'price-desc': list.sort((a, b) => parseFloat(b.price) - parseFloat(a.price)); break
    default:           list.sort((a, b) => (b.total_sales || 0) - (a.total_sales || 0)); break
  }
  return list
})

// HTML-entities decoderen (bijv. &amp; → &)
function decodeHtml(str) {
  const txt = document.createElement('textarea')
  txt.innerHTML = str
  return txt.value
}

const getCategoryIcon = (slug) => ({
  'zonnepanelen':    'bi-sun',
  'complete-sets':   'bi-box-seam',
  'laadpalen':       'bi-lightning-charge',
  'laadkabels':      'bi-plug',
  'micro-omvormers': 'bi-gear',
  'thuisbatterijen': 'bi-battery-half',
  'montage':         'bi-hammer',
  'kabels':          'bi-plug',
  'monitoring':      'bi-bar-chart-line',
  'accessoires':     'bi-bag',
  'batterijen':      'bi-battery-half',
}[slug] ?? 'bi-tag')

const usps = [
  { icon: 'bi-shield-check',    label: 'Officiële dealer',   sub: '100% origineel' },
  { icon: 'bi-award',           label: 'Premium kwaliteit',  sub: 'Langste levensduur' },
  { icon: 'bi-headset',         label: 'Expert advies',      sub: 'Ma–vr bereikbaar' },
  { icon: 'bi-truck',           label: 'Snelle levering',    sub: 'Morgen in huis' },
]

// ─── Data fetching ─────────────────────────────────────────────────────────────
async function fetchBrandData() {
  loading.value = true
  error.value   = false
  try {
    const res  = await fetch(`/_wc/v3/products/brands?slug=${brandSlug.value}`)
    if (!res.ok) throw new Error('Brand not found')
    const data = await res.json()
    if (!Array.isArray(data) || !data.length) throw new Error('Brand not found')
    brand.value = data[0]
    document.title = `${brand.value.name} producten | Plug2Charge`
    await fetchProducts()
  } catch {
    error.value = true
  } finally {
    loading.value = false
  }
}

async function fetchProducts() {
  productsLoading.value = true
  try {
    const res = await fetch(`/_wc/v3/products?brand=${brand.value.id}&per_page=100&status=publish`)
    if (res.ok) products.value = await res.json()
  } catch { /* silent */ } finally {
    productsLoading.value = false
  }
}

onMounted(fetchBrandData)
watch(() => route.params.brand, fetchBrandData)
watch(() => route.params.category, () => window.scrollTo({ top: 0, behavior: 'smooth' }))
</script>

<style scoped>
/* Verberg scrollbar op category-tabs */
.scrollbar-hide::-webkit-scrollbar { display: none; }
.scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
</style>
